import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    message: ''
  });
  
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const currentDate = new Date().toLocaleString('en-IN', { 
        timeZone: 'Asia/Kolkata',
        dateStyle: 'medium',
        timeStyle: 'short'
      });

      const dataToSend = {
        ...formData,
        date: currentDate
      };

      await fetch(
        'https://script.google.com/macros/s/AKfycbyErzX4o8kTYpLRNdgg8oQWPwuxbei7uJHOhPXFD3dK9aWPT-filGWI6FTe77Z9UA60/exec',
        {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(dataToSend)
        }
      );

      setStatus({
        type: 'success',
        message: 'Thank you! Your message has been sent successfully. We will get back to you soon.'
      });
      setFormData({ name: '', mobile: '', email: '', message: '' });
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again or contact us directly.'
      });
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-navy-900 to-navy-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Get In <span className="text-silver-300">Touch</span>
          </h2>
          <p className="text-xl text-silver-400 max-w-3xl mx-auto">
            Have a project in mind? Let's discuss how we can help bring your vision to life.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-navy-700/50 p-3 rounded-lg border border-silver-600/30">
                    <Phone className="text-silver-300" size={24} />
                  </div>
                  <div>
                    <p className="text-silver-400 text-sm mb-1">Phone</p>
                    <p className="text-white font-semibold">+91 98841 22553 / +91 93452 99622 / +91 63746 80867</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-navy-700/50 p-3 rounded-lg border border-silver-600/30">
                    <Mail className="text-silver-300" size={24} />
                  </div>
                  <div>
                    <p className="text-silver-400 text-sm mb-1">Email</p>
                    <p className="text-white font-semibold">globaldp12@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-navy-700/50 p-3 rounded-lg border border-silver-600/30">
                    <MapPin className="text-silver-300" size={24} />
                  </div>
                  <div>
                    <p className="text-silver-400 text-sm mb-1">Address</p>
                    <p className="text-white font-semibold">No.92/111, Jani Jhan Khan Road,<br />Royapettah, Chennai - 600 014.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-navy-700/30 backdrop-blur-sm p-8 rounded-xl border border-silver-600/30">
              <h4 className="text-xl font-bold text-white mb-4">Business Hours</h4>
              <div className="space-y-2 text-silver-300">
                <p className="flex justify-between">
                  <span>Monday - Saturday:</span>
                  <span className="text-white font-semibold">10:00 AM - 9:00 PM</span>
                </p>
                <p className="flex justify-between">
                  <span>Sunday:</span>
                  <span className="text-white font-semibold">Closed</span>
                </p>
              </div>
            </div>
          </div>

          <div className="bg-navy-700/50 backdrop-blur-sm p-8 rounded-xl border border-silver-600/30">
            <h3 className="text-2xl font-bold text-white mb-6">Contact Us</h3>
            
            {status.message && (
              <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
                status.type === 'success' 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-red-500/10 border border-red-500/30'
              }`}>
                {status.type === 'success' ? (
                  <CheckCircle className="text-green-400 flex-shrink-0" size={20} />
                ) : (
                  <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
                )}
                <p className={status.type === 'success' ? 'text-green-300' : 'text-red-300'}>
                  {status.message}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-silver-300 mb-2 font-medium">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-navy-800/50 border border-silver-600/30 rounded-lg text-white placeholder-silver-500 focus:outline-none focus:border-silver-400 transition-colors"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="mobile" className="block text-silver-300 mb-2 font-medium">
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  required
                  pattern="[0-9]{10}"
                  className="w-full px-4 py-3 bg-navy-800/50 border border-silver-600/30 rounded-lg text-white placeholder-silver-500 focus:outline-none focus:border-silver-400 transition-colors"
                  placeholder="10-digit mobile number"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-silver-300 mb-2 font-medium">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-navy-800/50 border border-silver-600/30 rounded-lg text-white placeholder-silver-500 focus:outline-none focus:border-silver-400 transition-colors"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-silver-300 mb-2 font-medium">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 bg-navy-800/50 border border-silver-600/30 rounded-lg text-white placeholder-silver-500 focus:outline-none focus:border-silver-400 transition-colors resize-none"
                  placeholder="Tell us about your project..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-silver-400 text-navy-900 px-8 py-4 rounded-lg font-semibold hover:bg-silver-300 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-navy-900"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
